import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class DentalHolidayCalendarTooltipService {
  private static readonly DateContentSelector =
    '.dental-holiday-calendar .mat-calendar-body-cell-content';
  private static readonly Code92133Class = 'dental-holiday-92133c';
  private static readonly Code92094Class = 'dental-holiday-92094c';

  private tooltipElement: HTMLDivElement | null = null;
  private activeCalendarView: string | null = null;

  private readonly onDocumentMouseMove = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const dateContent = target && typeof target.closest === 'function'
      ? target.closest(DentalHolidayCalendarTooltipService.DateContentSelector) as HTMLElement
      : null;

    if (!dateContent) {
      this.hide();
      return;
    }

    if (this.activeCalendarView !== 'month') {
      this.hide();
      return;
    }

    const calendarContent = dateContent.closest('.mat-calendar-content') as HTMLElement;
    const reflectedView = calendarContent
      ? calendarContent.getAttribute('ng-reflect-ng-switch')
      : null;
    if (reflectedView !== null && reflectedView !== 'month') {
      this.hide();
      return;
    }

    const dateCell = dateContent.closest('.mat-calendar-body-cell') as HTMLElement;
    const message = this.getMessage(dateCell);
    if (!message) {
      this.hide();
      return;
    }

    this.show(message.text, message.className, event.clientX, event.clientY);
  };

  private readonly onDocumentMouseLeave = (): void => {
    this.hide();
  };

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.document.addEventListener('mousemove', this.onDocumentMouseMove);
    this.document.addEventListener('mouseleave', this.onDocumentMouseLeave);
  }

  hide(): void {
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
  }

  setCalendarView(view: string | null): void {
    this.activeCalendarView = view;
    if (view !== 'month') {
      this.hide();
    }
  }

  private getMessage(dateCell: HTMLElement | null): { text: string; className: string } | null {
    if (!dateCell) {
      return null;
    }

    const codes: string[] = [];
    const classNames: string[] = [];
    if (dateCell.classList.contains(DentalHolidayCalendarTooltipService.Code92133Class)) {
      codes.push('92133C');
      classNames.push('dental-holiday-calendar-tooltip-92133c');
    }
    if (dateCell.classList.contains(DentalHolidayCalendarTooltipService.Code92094Class)) {
      codes.push('92094C');
      classNames.push('dental-holiday-calendar-tooltip-92094c');
    }

    return codes.length > 0
      ? { text: `可申報 ${codes.join('、')}`, className: classNames[0] }
      : null;
  }

  private show(message: string, className: string, clientX: number, clientY: number): void {
    const tooltip = this.ensureTooltip();
    tooltip.textContent = message;
    tooltip.className = `dental-holiday-calendar-tooltip ${className}`;
    tooltip.style.display = 'block';

    const viewport = this.document.defaultView;
    const viewportWidth = viewport?.innerWidth || 0;
    const viewportHeight = viewport?.innerHeight || 0;
    const offsetX = 21;
    const offsetY = 12;
    const tooltipRect = tooltip.getBoundingClientRect();

    let left = clientX + offsetX;
    let top = clientY + offsetY;
    if (viewportWidth > 0 && left + tooltipRect.width > viewportWidth) {
      left = Math.max(8, clientX - tooltipRect.width - offsetX);
    }
    if (viewportHeight > 0 && top + tooltipRect.height > viewportHeight) {
      top = Math.max(8, clientY - tooltipRect.height - offsetY);
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  private ensureTooltip(): HTMLDivElement {
    if (!this.tooltipElement) {
      this.tooltipElement = this.document.createElement('div');
      this.tooltipElement.className = 'dental-holiday-calendar-tooltip';
      this.tooltipElement.setAttribute('role', 'tooltip');
      this.tooltipElement.setAttribute('aria-hidden', 'true');
      this.document.body.appendChild(this.tooltipElement);
    }

    return this.tooltipElement;
  }
}
